import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiMe, apiLogin, apiRegister, apiGoogle, apiLogout, redeemBeta } from "@/lib/api";
import { toast } from "sonner";

const Ctx = createContext(null);
export const useAuth = () => useContext(Ctx);

const BETA_KEY = "adac_beta_code";

// Capture ?beta=CODE from the URL on first load and remember it so we can
// auto-grant Pro right after the user signs in / registers.
function captureBetaCode() {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("beta");
    if (code) {
      localStorage.setItem(BETA_KEY, code);
      params.delete("beta");
      const qs = params.toString();
      const url = window.location.pathname + (qs ? `?${qs}` : "") + window.location.hash;
      window.history.replaceState({}, "", url);
    }
  } catch { /* ignore */ }
}
captureBetaCode();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const tryRedeemBeta = useCallback(async (u) => {
    const code = localStorage.getItem(BETA_KEY);
    if (!code || !u || u.is_pro) {
      if (u?.is_pro) localStorage.removeItem(BETA_KEY);
      return u;
    }
    try {
      const upgraded = await redeemBeta(code);
      localStorage.removeItem(BETA_KEY);
      toast.success("Beta access unlocked — full Pro features enabled.");
      return upgraded;
    } catch {
      localStorage.removeItem(BETA_KEY);
      return u;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      let u = await apiMe();
      u = await tryRedeemBeta(u);
      setUser(u);
      if (u && !u.is_pro && ((u.usage_limit ?? 3) - (u.usage_count ?? 0)) === 1) {
        toast.warning("1 free export left", {
          description: "Subscribe for unlimited reports & submission packs.",
          action: { label: "Plans", onClick: () => window.location.assign("/pricing") },
          duration: 7000,
        });
      }
    } catch { setUser(null); }
  }, [tryRedeemBeta]);

  useEffect(() => { refresh().finally(() => setLoading(false)); }, [refresh]);

  useEffect(() => {
    const onUsage = () => refresh();
    window.addEventListener("usage-changed", onUsage);
    return () => window.removeEventListener("usage-changed", onUsage);
  }, [refresh]);

  const login = async (email, password) => { let u = await apiLogin(email, password); u = await tryRedeemBeta(u); setUser(u); return u; };
  const register = async (email, password, name) => { let u = await apiRegister(email, password, name); u = await tryRedeemBeta(u); setUser(u); return u; };
  const google = async (session_id) => { let u = await apiGoogle(session_id); u = await tryRedeemBeta(u); setUser(u); return u; };
  const logout = async () => { await apiLogout(); setUser(null); };

  return (
    <Ctx.Provider value={{ user, loading, login, register, google, logout, refresh, isPro: !!user?.is_pro }}>
      {children}
    </Ctx.Provider>
  );
}
